import { FilterCheckboxGroup, FilterConfig, MainFilter } from 'components/tables/list-table/ListTableTypes';
import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts.ts';
import { useLazyGetAllEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { useLazyGetPracticeAreasWithFiltersQuery } from 'store/api/practice-areas';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices.ts';
import { useLazyGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { ClassifierData } from 'types/classifiers';
import { RelationshipTypeCode } from 'types/contacts';
import { PracticeAreaType } from 'types/practice-areas';
import { TeamType } from 'types/teams';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators } from 'utils/matterOptionRenderer';
import { DebriefingLessonRelevance, DebriefingType } from './types';

export type DebriefingsTableVariant = 'global' | 'client-tab' | 'case-tab';

// The BE-whitelisted text-search selectors. The mockup placeholder mentions "description", but
// lesson descriptions are not searchable on the BE — case, client, project and leader names are.
const GLOBAL_SEARCH_SELECTORS = [
    { selector: 'matters.matter.caseNumber' },
    { selector: 'matters.matter.name' },
    { selector: 'client.name' },
    { selector: 'projectName' },
    { selector: 'sessionLeader.fullName' },
];

function myDebriefingsQuickFilter(): FilterCheckboxGroup {
    return {
        selector: 'myDebriefings',
        comperator: '==',
        filters: [
            {
                label: t('debriefing.filters.my-debriefings'),
                argument: 'true',
                tooltip: 'debriefing.filters.my-debriefings-tooltip',
            },
        ],
    };
}

function myCoreTeamQuickFilter(): FilterCheckboxGroup {
    return {
        selector: 'myCoreTeamDebriefings',
        comperator: '==',
        // The core-team arm of this filter only reaches case-specific debriefings, so narrowing the
        // type filter past them leaves it nothing of its own to match.
        getShouldDisable: ({ mainFilters }) => {
            const typeFilter = Object.entries(mainFilters).find(([key]) => key.startsWith('debriefingType:'))?.[1];
            if (typeFilter?.type !== 'multiselect' || typeFilter.selected.length === 0) {
                return false;
            }
            return !typeFilter.selected.some(({ value }) => value === DebriefingType.CASE_SPECIFIC);
        },
        filters: [
            {
                label: t('debriefing.filters.my-core-team-debriefings'),
                argument: 'true',
                tooltip: 'debriefing.filters.my-core-team-debriefings-tooltip',
            },
        ],
    };
}

interface DrawerOptions {
    categoryClassifier?: ClassifierData;
    businessAreaClassifier?: ClassifierData;
    /** Case-section and Client-section filters are omitted on the tabs where the parent fixes them. */
    variant: DebriefingsTableVariant;
}

function buildDrawerFilters({ categoryClassifier, businessAreaClassifier, variant }: DrawerOptions): MainFilter[] {
    const debriefingSection: MainFilter[] = [
        {
            type: 'multiselect',
            label: t('debriefing.filters.type'),
            selector: 'debriefingType',
            group: 'debriefing.filters.debriefing-section',
            tooltip: 'debriefing.filters.type-tooltip',
            options: Object.values(DebriefingType).map((type) => ({
                label: t(`debriefing.type.${type}`),
                value: type,
            })),
        },
        {
            type: 'checkboxgroup',
            label: t('debriefing.filters.client-rep-present'),
            selector: 'isClientRepPresent',
            group: 'debriefing.filters.debriefing-section',
            comperator: '==',
            multiple: false,
            displayDirection: 'row',
            filters: [{ label: t('yes'), argument: 'true' }],
        },
        {
            type: 'checkboxgroup',
            label: t('debriefing.filters.feedback-exists'),
            selector: 'feedbackExists',
            group: 'debriefing.filters.debriefing-section',
            comperator: '==',
            multiple: false,
            displayDirection: 'row',
            filters: [{ label: t('yes'), argument: 'true' }],
        },
        {
            type: 'dynamicmultiselect',
            label: t('debriefing.filters.participants'),
            selector: 'participants.employee.id',
            group: 'debriefing.filters.debriefing-section',
            paginated: {
                fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                searchField: 'fullName',
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
        {
            type: 'dynamicmultiselect',
            label: t('debriefing.filters.session-leader'),
            selector: 'sessionLeader.id',
            group: 'debriefing.filters.debriefing-section',
            paginated: {
                fetch: useLazyGetAllEmployeesListWithFiltersQuery,
                searchField: 'fullName',
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
        {
            type: 'daterange',
            label: t('debriefing.filters.date'),
            selector: 'debriefingDate',
            group: 'debriefing.filters.debriefing-section',
            shouldDisplayQuickPick: true,
        },
        {
            type: 'multiselect',
            label: t('debriefing.filters.relevance'),
            selector: 'lessons.relevance',
            group: 'debriefing.filters.debriefing-section',
            options: Object.values(DebriefingLessonRelevance).map((relevance) => ({
                label: t(`debriefing.relevance.${relevance}`),
                value: relevance,
            })),
        },
        {
            type: 'multiselect',
            label: t('debriefing.filters.category'),
            selector: 'lessons.categories.code',
            group: 'debriefing.filters.debriefing-section',
            options: categoryClassifier ? getFilterOptionsFromClassifier(categoryClassifier) : [],
        },
    ];

    const clientSection: MainFilter[] = [
        {
            type: 'checkboxgroup',
            label: t('debriefing.filters.my-clients'),
            selector: 'myClients',
            group: 'debriefing.filters.client-section',
            comperator: '==',
            multiple: false,
            displayDirection: 'row',
            filters: [{ label: t('yes'), argument: 'true' }],
        },
        {
            type: 'dynamicmultiselect',
            subType: 'contact',
            label: t('debriefing.filters.client-name'),
            selector: 'client.id',
            group: 'debriefing.filters.client-section',
            paginated: {
                fetch: useLazyGetMinContactsListWithFiltersQuery,
                searchField: ['name', 'partyNumber'],
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
                filters: `relationshipTypeCode==${RelationshipTypeCode.CLIENT}`,
            },
        },
        {
            type: 'multiselect',
            label: t('debriefing.filters.client-business-area'),
            selector: 'client.businessAreas.businessAreaCode',
            group: 'debriefing.filters.client-section',
            options: businessAreaClassifier ? getFilterOptionsFromClassifier(businessAreaClassifier) : [],
        },
        {
            type: 'dynamicmultiselect',
            label: t('debriefing.filters.client-sector-group'),
            selector: 'client.sectorGroups.id',
            group: 'debriefing.filters.client-section',
            paginated: {
                fetch: useLazyGetMinTeamsListWithFiltersQuery,
                searchField: 'name',
                filters: `teamTypeCode==${TeamType.SECTOR_GROUP};isParent==true`,
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
    ];

    const caseSection: MainFilter[] = [
        {
            type: 'dynamicmultiselect',
            label: t('debriefing.filters.case'),
            selector: 'matters.matter.id',
            group: 'debriefing.filters.case-section',
            getOptionStartAdornment: renderMatterIndicators,
            paginated: {
                fetch: useLazyGetMattersListQuery,
                searchField: ['fullName', 'clientParty.name'],
                getOptionIdentifier: 'id',
                getOptionLabel: 'fullName',
                sort: ['clientParty.name,asc', 'name,asc'],
                groupByField: 'clientParty.name',
            },
        },
        {
            type: 'dynamicmultiselect',
            label: t('debriefing.filters.practice-areas'),
            selector: 'matters.matter.practiceAreas.practiceArea.id',
            group: 'debriefing.filters.case-section',
            paginated: {
                fetch: useLazyGetPracticeAreasWithFiltersQuery,
                searchField: 'name',
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
                filters: `practiceAreaTypeCode==${PracticeAreaType.PRACTICE_AREA}`,
            },
        },
        {
            type: 'dynamicmultiselect',
            label: t('debriefing.filters.practice-group'),
            selector: 'matters.matter.practiceGroup.id',
            group: 'debriefing.filters.case-section',
            paginated: {
                fetch: useLazyGetMinTeamsListWithFiltersQuery,
                searchField: 'name',
                filters: `teamTypeCode==${TeamType.PRACTICE_GROUP};isParent==true`,
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
        {
            type: 'dynamicmultiselect',
            label: t('debriefing.filters.handled-by-office'),
            selector: 'matters.matter.handledByOffice.id',
            group: 'debriefing.filters.case-section',
            paginated: {
                fetch: useLazyGetMinOfficesWithFiltersQuery,
                searchField: 'name',
                getOptionIdentifier: 'id',
                getOptionLabel: 'name',
            },
        },
    ];

    const taskSection: MainFilter[] = [
        {
            type: 'checkboxgroup',
            label: t('debriefing.filters.has-tasks'),
            selector: 'hasTasks',
            group: 'debriefing.filters.task-connections-section',
            comperator: '==',
            multiple: false,
            displayDirection: 'row',
            filters: [{ label: t('yes'), argument: 'true' }],
        },
        {
            type: 'checkboxgroup',
            label: t('debriefing.filters.tasks-assigned-to-me'),
            selector: 'tasksAssignedToMe',
            group: 'debriefing.filters.task-connections-section',
            comperator: '==',
            multiple: false,
            displayDirection: 'row',
            filters: [{ label: t('yes'), argument: 'true' }],
        },
        {
            type: 'checkboxgroup',
            label: t('debriefing.filters.has-unfinished-tasks'),
            selector: 'hasUnfinishedTasks',
            group: 'debriefing.filters.task-connections-section',
            comperator: '==',
            multiple: false,
            displayDirection: 'row',
            filters: [{ label: t('yes'), argument: 'true' }],
        },
    ];

    switch (variant) {
        case 'global':
            return [...debriefingSection, ...clientSection, ...caseSection, ...taskSection];
        case 'client-tab':
            // Client is fixed by the tab; case filters still narrow the client's case-specific rows.
            return [...debriefingSection, ...caseSection, ...taskSection];
        case 'case-tab':
            return [...debriefingSection, ...taskSection];
    }
}

interface ConfigOptions extends DrawerOptions {
    matterId?: string;
    clientPartyId?: string;
}

export function getDebriefingsFilterConfig(options: ConfigOptions): FilterConfig {
    const { variant, matterId, clientPartyId } = options;

    // Exact passive shapes the BE list query is tested against.
    const passiveFilter =
        variant === 'case-tab' && matterId
            ? `matters.matter.id==${matterId}`
            : variant === 'client-tab' && clientPartyId
              ? `client.id==${clientPartyId};debriefingType=in=(CASE_SPECIFIC,CLIENT_SPECIFIC)`
              : undefined;

    return {
        quickFilters:
            variant === 'global' ? [myDebriefingsQuickFilter(), myCoreTeamQuickFilter()] : [myDebriefingsQuickFilter()],
        filters: buildDrawerFilters(options),
        searchBarSelectors:
            variant === 'global'
                ? GLOBAL_SEARCH_SELECTORS
                : GLOBAL_SEARCH_SELECTORS.filter(({ selector }) => !selector.startsWith('client.')),
        passiveFilter,
    };
}
